import { useForm } from "react-hook-form";
import { User, useUsers } from "@/lib/api/user.ts";
import type { IdentitySystemRole } from "@/lib/utils/system-roles";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ChevronsUpDown, PlusIcon } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form.tsx";
import { CheckIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  CreateMentorshipInput,
  createMentorshipSchema,
  useCreateMentorship,
} from "@/lib/api/mentorships.ts";
import { AxiosError } from "axios";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { useDebounce } from "@/lib/hooks/use-debounce.ts";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { cn } from "@/lib/utils.ts";
import { Loader2 } from "lucide-react";

interface AddMentorshipProps {
  userId: string;
  isMentor: boolean;
  isMentee: boolean;
  disabled?: boolean;
  triggerLabel?: string;
  triggerClassName?: string;
  iconClassName?: string;
}

function getUserDisplayName(
  user: Pick<User, "name" | "first_name" | "last_name" | "phone">,
): string {
  if (user.name) return user.name;
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
  if (fullName) return fullName;
  return user.phone ?? "Unknown";
}

interface UserComboboxProps {
  value?: string;
  onChange: (id: string, user: User) => void;
  role: IdentitySystemRole;
  enabled: boolean;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  selectedDisplayName?: string;
}

function UserCombobox({
  value,
  onChange,
  role,
  enabled,
  placeholder,
  searchPlaceholder,
  emptyText,
  selectedDisplayName,
}: UserComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 250);

  const { data, isFetching } = useUsers({
    page: 1,
    perPage: 50,
    search: debouncedSearch.trim() || undefined,
    assignedRole: role,
    enabled,
    status: "active",
  });

  const users = data?.users?.filter((user) => !user.deactivated) ?? [];

  const isSearchPending = search !== debouncedSearch;
  const isLoadingIndicatorVisible = isFetching || isSearchPending;
  const isInitialLoad = isLoadingIndicatorVisible && !data;
  const showEmptyState =
    !isLoadingIndicatorVisible && data && users.length === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between rounded-xl font-normal capitalize"
        >
          <span
            className={cn(
              "truncate",
              !selectedDisplayName && "text-muted-foreground",
            )}
          >
            {selectedDisplayName ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <div className="relative">
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              className="pr-8"
            />
            {isLoadingIndicatorVisible && (
              <Loader2
                className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
            )}
          </div>
          <CommandList className="min-h-[220px]">
            {isInitialLoad && (
              <div className="flex flex-col gap-1 p-1">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-2 py-1.5"
                  >
                    <Skeleton className="size-4 shrink-0 rounded-sm" />
                    <Skeleton
                      className="h-4"
                      style={{ width: `${45 + ((index * 13) % 40)}%` }}
                    />
                  </div>
                ))}
              </div>
            )}

            {showEmptyState && (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            )}

            {!isInitialLoad && users.length > 0 && (
              <CommandGroup
                className={cn(
                  "transition-opacity",
                  isLoadingIndicatorVisible && "opacity-60",
                )}
              >
                {users.map((user) => {
                  const displayName = getUserDisplayName(user);
                  const isSelected = value === user.id;
                  return (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={() => {
                        onChange(user.id, user);
                        setOpen(false);
                      }}
                      className="capitalize"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 size-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {displayName}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function AddMentorship({
  userId,
  isMentor,
  isMentee,
  disabled,
  triggerLabel = "Add Mentorship",
  triggerClassName,
  iconClassName = "mr-2 size-4",
}: AddMentorshipProps) {
  const ability = useAbility();
  const canCreateMentorships =
    hasPrivilegedAccessFromAbility(ability) &&
    ability.can("create", "mentorships");

  const { mutateAsync: createMentorship, isPending } = useCreateMentorship();
  const [open, setOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState<string | undefined>(
    undefined,
  );

  const form = useForm<CreateMentorshipInput>({
    resolver: zodResolver(createMentorshipSchema),
    defaultValues: {
      mentorId: isMentor ? userId : undefined,
      menteeId: isMentee ? userId : undefined,
    },
  });

  async function onSubmit(input: CreateMentorshipInput) {
    try {
      await createMentorship(input);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const field = isMentor ? "menteeId" : "mentorId";
        form.setError(field, {
          message:
            "This teen already has an active mentorship. Deactivate it first.",
        });
      }
      return;
    }

    form.reset();
    setSelectedUserName(undefined);
    setOpen(false);
  }

  useEffect(() => {
    if (!open) {
      form.reset();
      setSelectedUserName(undefined);
    }
  }, [open, form]);

  if (!canCreateMentorships) {
    return null;
  }

  const MentorField = (
    <FormField
      control={form.control}
      name="mentorId"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <UserCombobox
              value={field.value}
              onChange={(id, user) => {
                field.onChange(id);
                setSelectedUserName(getUserDisplayName(user));
              }}
              role="mentor"
              enabled={isMentee}
              placeholder="Select a mentor"
              searchPlaceholder="Search mentors..."
              emptyText="No mentors found"
              selectedDisplayName={field.value ? selectedUserName : undefined}
            />
          </FormControl>
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );

  const MenteeField = (
    <FormField
      control={form.control}
      name="menteeId"
      render={({ field }) => (
        <FormItem>
          <FormControl>
            <UserCombobox
              value={field.value}
              onChange={(id, user) => {
                field.onChange(id);
                setSelectedUserName(getUserDisplayName(user));
              }}
              role="mentee"
              enabled={isMentor}
              placeholder="Select a teen"
              searchPlaceholder="Search teens by name, phone, or email..."
              emptyText="No teens found"
              selectedDisplayName={field.value ? selectedUserName : undefined}
            />
          </FormControl>
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={triggerClassName ? "outline" : "default"}
          className={
            triggerClassName ?? "bg-black hover:bg-gray-700"
          }
          disabled={disabled}
        >
          <PlusIcon className={iconClassName} aria-hidden="true" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Mentorship</DialogTitle>
          <DialogDescription>
            Add a new mentorship between a mentor and a mentee
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            {isMentee ? MentorField : MenteeField}

            <DialogFooter className="gap-2 pt-2 sm:space-x-0">
              <Button disabled={isPending}>
                {isPending ? (
                  <ReloadIcon
                    className="mr-2 size-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckIcon className="mr-2 size-4" aria-hidden="true" />
                )}
                {isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
